import { useEffect, useState, useCallback } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Star, Loader2, Send, ThumbsUp, ThumbsDown, MessageCircle, Copy,
  AlertTriangle, ShieldCheck, CheckCircle2,
} from "lucide-react";
import {
  reputationSendSurvey, reputationListFeedback, reputationOwnerReply,
} from "@/lib/api";
import { toast } from "sonner";
import InfographicHero from "@/components/InfographicHero";

/** Reputation Center — /reputation
 *  Left: send review-request form + list of past surveys
 *  Right: feedback inbox with sentiment, urgent-first, owner-response drafts,
 *          consent-gated "Publish as testimonial" action.
 */

const SENTIMENT_TONE = {
  positive: "border-emerald-200 bg-emerald-50 text-emerald-800",
  neutral: "border-amber-200 bg-amber-50 text-amber-900",
  negative: "border-rose-200 bg-rose-50 text-rose-800",
};

export default function ReputationCenterPage() {
  const [survey, setSurvey] = useState({
    customer_name: "", customer_email: "", customer_phone: "",
    channel: "email", template: "standard",
  });
  const [sending, setSending] = useState(false);
  const [lastShare, setLastShare] = useState(null);
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({});
  const [filter, setFilter] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await reputationListFeedback(filter);
      setItems(r.feedback || []);
      setSummary(r.summary || {});
    } catch (e) {
      toast.error("Sign in to view reputation data.");
    } finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { document.title = "Reputation Center — WebsiteTech.pro"; }, []);
  useEffect(() => { load(); }, [load]);

  const sendReview = async () => {
    if (!survey.customer_name.trim()) {
      toast.error("Customer name required");
      return;
    }
    setSending(true);
    try {
      const r = await reputationSendSurvey(survey);
      const shareUrl = `${window.location.origin}${r.public_url}`;
      setLastShare(shareUrl);
      try { await navigator.clipboard.writeText(shareUrl); } catch { /* ignore */ }
      toast.success("Review link created and copied");
      setSurvey({ ...survey, customer_name: "",
                  customer_email: "", customer_phone: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create link");
    } finally { setSending(false); }
  };

  const reply = async (fid, publishAsTestimonial) => {
    const body = (drafts[fid] || "").trim();
    if (!body) { toast.error("Draft a response first"); return; }
    setBusyId(fid);
    try {
      await reputationOwnerReply(fid, body, publishAsTestimonial);
      toast.success(publishAsTestimonial
        ? "Response sent + testimonial published"
        : "Response saved");
      setDrafts({ ...drafts, [fid]: "" });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setBusyId(null); }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="reputation-page">
      <Navbar />
      <section className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-6xl px-4 py-8">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-orange-600">
            <Star className="h-4 w-4" /> Reputation & Feedback Center
          </div>
          <h1 className="mt-2 font-heading text-3xl font-black text-slate-900 sm:text-4xl">
            Ask for feedback. <span className="text-orange-600">Publish only what customers approve.</span>
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-slate-600">
            Consent-gated review requests, sentiment analysis, private
            service-recovery for unhappy customers, and one-tap testimonial
            publishing when the customer explicitly opts in.
          </p>
          <div className="mt-6">
            <InfographicHero slug="reputation-survey-center" hint={false} />
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <SummaryPill label="Total" value={summary.total ?? 0}
              onClick={() => setFilter(null)} active={!filter} testid="rep-filter-all" />
            <SummaryPill label="Positive" value={summary.positive ?? 0}
              onClick={() => setFilter("positive")} active={filter === "positive"}
              tone="emerald" testid="rep-filter-positive" />
            <SummaryPill label="Neutral" value={((summary.total ?? 0) - (summary.positive ?? 0) - (summary.urgent_needs_attention ?? 0))}
              onClick={() => setFilter("neutral")} active={filter === "neutral"}
              tone="amber" testid="rep-filter-neutral" />
            <SummaryPill label="Urgent" value={summary.urgent_needs_attention ?? 0}
              onClick={() => setFilter("negative")} active={filter === "negative"}
              tone="rose" testid="rep-filter-urgent" />
            <SummaryPill label="Published" value={summary.published ?? 0}
              tone="sky" testid="rep-count-published" />
          </div>
        </div>
      </section>

      <section className="mx-auto grid max-w-6xl gap-6 px-4 py-8 lg:grid-cols-[380px_1fr]">
        {/* Send review request */}
        <Card className="border-slate-200 bg-white p-4 h-fit" data-testid="rep-survey-form">
          <div className="font-heading text-lg font-bold text-slate-900">
            Send a review request
          </div>
          <div className="mt-1 text-xs text-slate-500">
            Creates a consent-safe survey link. Nothing publishes until the
            customer opts in and you approve.
          </div>
          <div className="mt-4 space-y-3">
            <Input value={survey.customer_name}
              onChange={(e) => setSurvey({ ...survey, customer_name: e.target.value })}
              placeholder="Customer name"
              data-testid="rep-survey-name" />
            <Input value={survey.customer_email}
              onChange={(e) => setSurvey({ ...survey, customer_email: e.target.value })}
              placeholder="Email (optional)"
              data-testid="rep-survey-email" />
            <Input value={survey.customer_phone}
              onChange={(e) => setSurvey({ ...survey, customer_phone: e.target.value })}
              placeholder="Phone / WhatsApp (optional)"
              data-testid="rep-survey-phone" />
            <div className="flex gap-2 text-xs">
              {["email", "sms", "whatsapp"].map((c) => (
                <button key={c}
                  onClick={() => setSurvey({ ...survey, channel: c })}
                  className={`rounded-full border px-3 py-1 ${survey.channel === c ? "border-orange-400 bg-orange-50 text-orange-800" : "border-slate-200 text-slate-500"}`}
                  data-testid={`rep-survey-channel-${c}`}
                >
                  {c}
                </button>
              ))}
            </div>
            <Button onClick={sendReview} disabled={sending}
              className="w-full bg-orange-600 text-white hover:bg-orange-700"
              data-testid="rep-survey-send">
              {sending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Send className="mr-1 h-4 w-4" />}
              Create review link
            </Button>
            {lastShare && (
              <div className="mt-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs" data-testid="rep-share-url">
                <div className="font-semibold text-emerald-900">Public survey link</div>
                <div className="mt-1 truncate font-mono text-[11px] text-emerald-800">{lastShare}</div>
                <Button size="sm" variant="ghost" className="mt-2 h-7 text-emerald-800"
                  onClick={() => { navigator.clipboard.writeText(lastShare); toast.success("Copied"); }}>
                  <Copy className="mr-1 h-3 w-3" /> Copy
                </Button>
              </div>
            )}
          </div>
        </Card>

        {/* Feedback stream */}
        <div className="space-y-3">
          {loading && <div className="text-slate-500">Loading…</div>}
          {!loading && items.length === 0 && (
            <Card className="p-8 text-sm text-slate-500" data-testid="rep-empty">
              No feedback yet. Send a review request to start collecting.
            </Card>
          )}
          {items.map((f) => {
            const isNeg = f.sentiment === "negative";
            return (
              <Card key={f._id}
                className={`border p-4 ${SENTIMENT_TONE[f.sentiment]}`}
                data-testid={`rep-item-${f._id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2 font-semibold">
                      {f.customer_name}
                      <span className="text-xs opacity-70">· rating {f.rating}/5</span>
                      {isNeg && <Badge variant="outline" className="border-rose-400 text-rose-700"><AlertTriangle className="mr-1 h-3 w-3" /> urgent</Badge>}
                      {f.published && <Badge variant="outline" className="border-emerald-400 text-emerald-700"><CheckCircle2 className="mr-1 h-3 w-3" /> published</Badge>}
                    </div>
                    <div className="mt-1 text-sm">{f.comment || "—"}</div>
                    <div className="mt-1 text-[11px] uppercase tracking-widest opacity-70">
                      {f.sentiment} · {new Date(f.created_at).toLocaleString()}
                    </div>
                  </div>
                  <div className="text-[10px] font-mono opacity-70">
                    consent: {f.consent_public_publish ? "✓ public" : "× private"}
                  </div>
                </div>

                {!f.owner_response ? (
                  <div className="mt-3 border-t border-white/40 pt-3">
                    <Textarea
                      value={drafts[f._id] || ""}
                      onChange={(e) => setDrafts({ ...drafts, [f._id]: e.target.value })}
                      placeholder="Draft an owner response — will be sent for approval..."
                      className="min-h-[60px] bg-white/70"
                      data-testid={`rep-reply-input-${f._id}`}
                    />
                    <div className="mt-2 flex flex-wrap gap-2">
                      <Button size="sm" variant="outline"
                        onClick={() => reply(f._id, false)}
                        disabled={busyId === f._id}
                        data-testid={`rep-reply-save-${f._id}`}>
                        {busyId === f._id ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <MessageCircle className="mr-1 h-3 w-3" />}
                        Save response
                      </Button>
                      <Button size="sm"
                        onClick={() => reply(f._id, true)}
                        disabled={busyId === f._id || !f.consent_public_publish}
                        className="bg-emerald-600 text-white hover:bg-emerald-700"
                        data-testid={`rep-publish-${f._id}`}>
                        <ShieldCheck className="mr-1 h-3 w-3" />
                        {f.consent_public_publish ? "Publish as testimonial" : "No public consent"}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="mt-3 rounded-lg border border-white/40 bg-white/60 p-3 text-sm">
                    <div className="text-[10px] font-semibold uppercase tracking-widest opacity-70">Owner response</div>
                    <div className="mt-1">{f.owner_response}</div>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      </section>

      <Footer />
    </div>
  );
}

function SummaryPill({ label, value, onClick, active, tone, testid }) {
  const toneCls = {
    emerald: "border-emerald-300 bg-emerald-50 text-emerald-800",
    amber: "border-amber-300 bg-amber-50 text-amber-800",
    rose: "border-rose-300 bg-rose-50 text-rose-800",
    sky: "border-sky-300 bg-sky-50 text-sky-800",
  }[tone] || "border-slate-200 text-slate-700";
  return (
    <button
      onClick={onClick}
      className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${toneCls} ${active ? "ring-2 ring-offset-1 ring-orange-400" : ""} ${onClick ? "hover:opacity-80" : "cursor-default"}`}
      data-testid={testid}
    >
      {label}: <b>{value}</b>
    </button>
  );
}
